import React, { useState, useEffect, useCallback } from 'react';
import {
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  LanguageIcon,
} from '@heroicons/react/24/outline';
import { Language, SystemMessage } from '../types';
import { languagesApi, systemMessagesApi, handleApiError } from '../utils/api';

const CATEGORY_LABELS: Record<string, string> = {
  session_start: 'Session Start',
  errors: 'Errors',
  limits: 'Message Limits',
  session_expiry: 'Session Expiry',
  media: 'Media',
  whatsapp: 'WhatsApp',
  commands: 'Bot Commands',
};

export function SystemMessages() {
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedLanguageCode, setSelectedLanguageCode] = useState('en');
  const [languages, setLanguages] = useState<Language[]>([
    { id: 2, name: 'English', code: 'en', usage: 'translation', createdAt: '', updatedAt: null },
  ]);
  const [search, setSearch] = useState('');

  // Editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await systemMessagesApi.getAll(
        selectedCategory || undefined,
        selectedLanguageCode
      );
      setMessages(response.data);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedLanguageCode]);

  const loadLanguages = useCallback(async () => {
    try {
      const response = await languagesApi.getTranslationLanguages();
      const normalized = response.data.map((language) => ({
        ...language,
        code: language.code.toLowerCase(),
      }));
      const hasEnglish = normalized.some((language) => language.code === 'en');
      setLanguages(hasEnglish ? normalized : [
        { id: 2, name: 'English', code: 'en', usage: 'translation', createdAt: '', updatedAt: null },
        ...normalized,
      ]);
    } catch (err) {
      setError(handleApiError(err));
    }
  }, []);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const categories = Array.from(new Set(messages.map((m) => m.category))).sort();

  const filteredMessages = messages.filter((m) => {
    if (search) {
      const q = search.toLowerCase();
      return (
        m.key.toLowerCase().includes(q) ||
        m.label.toLowerCase().includes(q) ||
        m.content.toLowerCase().includes(q) ||
        m.default_content.toLowerCase().includes(q) ||
        (m.description || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleEdit = (msg: SystemMessage) => {
    setEditingKey(msg.key);
    setEditContent(msg.content);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditContent('');
  };

  const handleSave = async () => {
    if (!editingKey) return;
    try {
      setSaving(true);
      const response = selectedLanguageCode === 'en'
        ? await systemMessagesApi.update(editingKey, editContent)
        : await systemMessagesApi.updateTranslation(editingKey, selectedLanguageCode, editContent);
      setMessages((prev) =>
        prev.map((m) => (m.key === editingKey ? response.data : m))
      );
      setSuccessMessage(selectedLanguageCode === 'en' ? 'Message updated successfully' : 'Translation updated successfully');
      setEditingKey(null);
      setEditContent('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTranslation = async (msg: SystemMessage) => {
    if (selectedLanguageCode === 'en') return;
    try {
      const response = await systemMessagesApi.deleteTranslation(msg.key, selectedLanguageCode);
      setMessages((prev) =>
        prev.map((m) => (m.key === msg.key ? response.data : m))
      );
      if (editingKey === msg.key) {
        setEditingKey(null);
        setEditContent('');
      }
      setSuccessMessage('Translation removed successfully');
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleToggle = async (msg: SystemMessage) => {
    try {
      const response = await systemMessagesApi.toggle(msg.key, !msg.is_active, selectedLanguageCode);
      setMessages((prev) =>
        prev.map((m) => (m.key === msg.key ? response.data : m))
      );
      setSuccessMessage(`Message "${msg.label}" ${!msg.is_active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  // Group by category
  const grouped = filteredMessages.reduce<Record<string, SystemMessage[]>>((acc, m) => {
    const cat = m.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Messages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Edit the automated messages that are sent to clients and agents.
          </p>
        </div>
        <button
          onClick={loadMessages}
          className="mt-3 sm:mt-0 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full rounded-md border-gray-300 pl-10 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat] || cat}
            </option>
          ))}
        </select>
        <div className="relative sm:w-56">
          <LanguageIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <select
            value={selectedLanguageCode}
            onChange={(e) => {
              setSelectedLanguageCode(e.target.value);
              setEditingKey(null);
              setEditContent('');
            }}
            className="w-full rounded-md border-gray-300 pl-10 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name} ({language.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Messages grouped by category */}
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, msgs]) => (
          <div key={category} className="bg-white shadow rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {CATEGORY_LABELS[category] || category}
              </h2>
            </div>
            <ul className="divide-y divide-gray-200">
              {msgs.map((msg) => (
                <li key={msg.key} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {msg.label}
                        </h3>
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {msg.key}
                        </span>
                        {msg.is_active ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 text-red-400" />
                        )}
                        {selectedLanguageCode !== 'en' && (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                            msg.has_translation
                              ? 'bg-green-50 text-green-700'
                              : 'bg-amber-50 text-amber-700'
                          }`}>
                            {msg.has_translation ? 'Translated' : 'Fallback'}
                          </span>
                        )}
                      </div>
                      {msg.description && (
                        <p className="mt-1 text-xs text-gray-500">{msg.description}</p>
                      )}

                      {editingKey === msg.key ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={Math.max(4, editContent.split('\n').length + 1)}
                            className="block w-full rounded-md border-gray-300 text-sm font-mono focus:border-primary-500 focus:ring-primary-500"
                          />
                          {selectedLanguageCode !== 'en' && (
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                              <div className="text-xs font-medium text-gray-500">English source</div>
                              <pre className="mt-1 whitespace-pre-wrap text-xs text-gray-600 font-mono max-h-32 overflow-auto">
                                {msg.default_content}
                              </pre>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancel}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <pre className="mt-2 whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 rounded-md p-3 font-mono max-h-48 overflow-auto">
                          {msg.content}
                        </pre>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex flex-col gap-2 flex-shrink-0">
                      {editingKey !== msg.key && (
                        <button
                          onClick={() => handleEdit(msg)}
                          className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleToggle(msg)}
                        className={`text-xs font-medium ${
                          msg.is_active
                            ? 'text-red-600 hover:text-red-800'
                            : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {msg.is_active ? 'Disable' : 'Enable'}
                      </button>
                      {selectedLanguageCode !== 'en' && msg.has_translation && editingKey !== msg.key && (
                        <button
                          onClick={() => handleDeleteTranslation(msg)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete Translation
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}

      {filteredMessages.length === 0 && !loading && (
        <div className="text-center py-12">
          <ChatBubbleBottomCenterTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {search ? 'Try a different search term.' : 'System messages will appear after the first deployment.'}
          </p>
        </div>
      )}
    </div>
  );
}
